import { useEffect, useState } from "react";
import {
	AlertType,
	AuditLog,
	AuditLogSearch,
	Doctor,
	PatientSearchResponse
} from "../models";
import { format } from "date-fns/format";
import { useAlertContext, useClinicContext, useUserContext } from "../contexts";
import {
	BuildUrl,
	convertUTCDateToLocalDate,
	localToUtcString
} from "../helpers";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../constants";
import { useHttp } from "../hooks";
import { AuditLogsFilter, AuditLogsTable } from "../components/AuditLogs";
import CircularProgress from "@mui/material/CircularProgress";

const initialAuditLogSearch = {
	clinicId: 0,
	doctorId: undefined,
	patientId: undefined,
	username: "",
	applicationScreen: "",
	action: "",
	fromDate: format(new Date(), "yyyy-MM-dd"),
	toDate: `${format(new Date(), "yyyy-MM-dd")}T23:59:59`
};

export const AuditLogs = () => {
	const { clinics } = useClinicContext();
	const { account } = useUserContext();
	const { instance } = useMsal();

	const [filters, setFilters] = useState<AuditLogSearch>(initialAuditLogSearch);
	const { showAlert, clearAlert } = useAlertContext();
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
	const [doctorSearch, setDoctorSearch] = useState<Doctor | undefined>(
		undefined
	);
	const [patientSearch, setPatientSearch] = useState<
		PatientSearchResponse | undefined
	>(undefined);
	const [usernameSearch, setUsernameSearch] = useState("");

	const { data: screens, isError: isScreensError } = useHttp<string[]>(
		BuildUrl("AuditLog/screens"),
		{
			method: "GET",
			autoFetch: true
		}
	);

	const { data: actions, isError: isActionsError } = useHttp<string[]>(
		BuildUrl("AuditLog/actions"),
		{
			method: "GET",
			autoFetch: true
		}
	);

	useEffect(() => {
		if (isScreensError) {
			showAlert({
				message: "Screens could not be retrieved",
				alertType: AlertType.error
			});
		}
	}, [isScreensError, showAlert]);

	useEffect(() => {
		if (isActionsError) {
			showAlert({
				message: "Actions could not be retrieved",
				alertType: AlertType.error
			});
		}
	}, [isActionsError, showAlert]);

	const retrieveLogs = async (filters: AuditLogSearch) => {
		if (
			!filters.clinicId &&
			!filters.doctorId &&
			!filters.patientId &&
			!filters.username &&
			!filters.applicationScreen &&
			!filters.action &&
			!filters.fromDate &&
			!filters.toDate
		) {
			showAlert({
				message:
					"At least one filter (ClinicId, DoctorId, PatientId, Username, ApplicationScreen, Action, FromDate and ToDate) must be supplied.",
				alertType: AlertType.error
			});
			return;
		}

		try {
			setIsError(false);
			setIsLoading(true);
			const requestUrl = BuildUrl("AuditLog/Search");
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});

			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};

			const body = {
				...filters,
				fromDate: localToUtcString(filters.fromDate ?? ""),
				toDate: localToUtcString(filters.toDate ?? "")
			};

			const response = await fetch(requestUrl, {
				method: "POST",
				headers,
				body: JSON.stringify(body)
			});

			if (!response.ok) {
				throw new Error("Failed to create audit log");
			}
			const result = await response.json();
			const auditLogs = result.map((auditLog: AuditLog) => ({
				...auditLog,
				createdOn: convertUTCDateToLocalDate(auditLog.createdOn)
			}));
			setAuditLogs(auditLogs);
		} catch (error) {
			setIsError(true);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		isError
			? showAlert({
					message: "Error retrieving logs",
					alertType: AlertType.error
				})
			: clearAlert();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isError]);

	const handleSearchClick = () => {
		retrieveLogs(filters);
	};

	const handleClearClick = () => {
		setFilters(initialAuditLogSearch);
		setUsernameSearch("");
		setDoctorSearch(undefined);
		setPatientSearch(undefined);
		setAuditLogs([]);
	};

	if (isLoading) {
		return (
			<div className="flex icon-loading">
				<CircularProgress size={70} />
			</div>
		);
	} else if (isError) {
		return <></>;
	} else {
		return (
			<>
				<AuditLogsFilter
					clinics={clinics ?? []}
					screens={screens ?? []}
					actions={actions ?? []}
					filters={filters}
					setFilters={setFilters}
					handleSearchClick={handleSearchClick}
					doctorSearch={doctorSearch}
					setDoctorSearch={setDoctorSearch}
					patientSearch={patientSearch}
					setPatientSearch={setPatientSearch}
					usernameSearch={usernameSearch}
					setUsernameSearch={setUsernameSearch}
					handleClearClick={handleClearClick}
				/>
				<AuditLogsTable auditLogs={auditLogs} />
			</>
		);
	}
};
